
import type { ChordValidation, TimeSignature, SoundProfile } from "../../types";
import { tokensToStructuredGrid } from "../../types";
import { TFHarmonyEngine } from "./tfHarmonyBackend";

const tfEngine = TFHarmonyEngine.getInstance();

/**
 * GSC CHORD DETECTOR - V4.1 (CONSISTENCY PATCH)
 */
export async function validateGridFromChroma(
  stuChroma: number[][],
  gridTokens: string[],
  opts: {
    bpm: number;
    timeSignature: TimeSignature;
    frameHopSec: number;
    durationSec: number;
    lagMs: number;
    soundProfile: SoundProfile;
    refChroma?: number[][];
  }
): Promise<{ gridValidation: ChordValidation[]; harmonyFromGrid: number | null }> {
  
  const structuredGrid = tokensToStructuredGrid(gridTokens);
  if (structuredGrid.length === 0) return { gridValidation: [], harmonyFromGrid: null };

  const { bpm, timeSignature, frameHopSec, lagMs, soundProfile } = opts;
  const beatsPerBar = parseInt(timeSignature.split('/')[0]) || 4;
  const secPerBar = (60 / bpm) * beatsPerBar;
  
  const validation: ChordValidation[] = [];
  let totalScoreSum = 0;
  let totalChordCount = 0;

  await tfEngine.init();

  for (let mIdx = 0; mIdx < structuredGrid.length; mIdx++) {
      const chords = structuredGrid[mIdx];
      const measureStart = (mIdx * secPerBar) + (lagMs / 1000);

      for (let cIdx = 0; cIdx < chords.length; cIdx++) {
          const expected = chords[cIdx];
          const chordDuration = secPerBar / chords.length;
          
          // Fenêtre d'analyse décalée : on ignore les 150ms de transition
          const analysisStart = measureStart + (cIdx * chordDuration) + 0.15;
          const analysisEnd = analysisStart + (chordDuration * 0.7); 

          const startFrame = Math.floor(analysisStart / frameHopSec);
          const endFrame = Math.ceil(analysisEnd / frameHopSec);

          let segment = (stuChroma && stuChroma.length > 0) 
            ? stuChroma.slice(Math.max(0, startFrame), Math.min(stuChroma.length, endFrame)) 
            : [];
          
          if (segment.length === 0 && stuChroma?.length) {
              const fallbackIdx = Math.max(0, Math.min(stuChroma.length - 1, Math.round(analysisStart / frameHopSec)));
              segment = [stuChroma[fallbackIdx]];
          }

          let bestFrameScore = 0;
          let detectedSymbol = "None";

          if (segment.length > 0) {
              // LOGIQUE "PEAK PERFORMANCE" : On retient votre meilleur instant sur l'accord
              for (const frame of segment) {
                  const { score } = tfEngine.evaluateMatch(frame, expected, soundProfile);
                  if (score > bestFrameScore) {
                      bestFrameScore = score;
                  }
              }

              // Si le score est très bas, on tente une prédiction globale pour voir ce qui a été joué
              if (bestFrameScore < 45) {
                  const avgChroma = new Array(12).fill(0);
                  segment.forEach(f => f.forEach((v, i) => avgChroma[i] += v));
                  const normalized = avgChroma.map(v => v / segment.length);
                  const prediction = await tfEngine.predict(normalized, soundProfile);
                  detectedSymbol = prediction.symbol;
              } else {
                  detectedSymbol = expected;
              }
          }

          const safeScore = Math.round(Math.max(0, Math.min(100, bestFrameScore)));

          validation.push({
              measureIndex: mIdx,
              expected,
              detected: detectedSymbol, 
              score: safeScore,
              isCorrect: safeScore >= 50,
              confidence: safeScore / 100
          });

          totalScoreSum += safeScore;
          totalChordCount++;
      }
  }

  // Moyenne arithmétique exacte des "meilleurs moments" pour la cohérence UI
  const harmonyFromGrid = totalChordCount > 0 ? Math.round(totalScoreSum / totalChordCount) : 0;
  
  return { 
      gridValidation: validation, 
      harmonyFromGrid 
  };
}
